import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Users, Calendar, DollarSign, Shield, CheckCircle } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
      {/* Header/Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <Trophy className="w-8 h-8 text-green-600" />
              <div className="flex flex-col">
                <span className="text-2xl font-bold leading-none">
                  <span className="text-green-600">Club</span><span className="text-blue-600">Hudl</span>
                </span>
                <span className="text-[9px] text-gray-500 font-medium tracking-wide uppercase">Your Club Operations Made Easy</span>
              </div>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => navigate('/register-club')}
                className="hidden md:block text-green-600 border border-green-600 px-6 py-2 rounded-lg font-semibold hover:bg-green-50 transition-all"
              >
                Register your Club
              </button>
              <button
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                Login / Signup
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Manage Your Sports Club
            <span className="block bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mt-2">
              With Ease
            </span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            The complete solution for sports club management. From teams and tournaments to games and expenses - all in one place.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">
          Everything You Need to Run Your Club
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Feature 1 */}
          <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-900">Team Management</h3>
            <p className="text-gray-600">
              Create and organize multiple teams. Manage player rosters, track availability, and coordinate squad selections effortlessly.
            </p>
          </div>

          {/* Feature 2 */}
          <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <Trophy className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-900">Tournament Tracking</h3>
            <p className="text-gray-600">
              Organize tournaments, schedule matches, and track your club's performance across multiple competitions.
            </p>
          </div>

          {/* Feature 3 */}
          <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-900">Game Scheduling</h3>
            <p className="text-gray-600">
              Schedule games, set venues, and send automated reminders to players. Keep everyone informed and ready.
            </p>
          </div>

          {/* Feature 4 */}
          <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <DollarSign className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-900">Expense Management</h3>
            <p className="text-gray-600">
              Track club expenses, upload receipts, and manage payments. Generate reports for complete financial transparency.
            </p>
          </div>

          {/* Feature 5 */}
          <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-900">Role-Based Access</h3>
            <p className="text-gray-600">
              Secure access control with distinct permissions for super admins, club admins, and players. Everyone sees what they need.
            </p>
          </div>

          {/* Feature 6 */}
          <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-teal-600" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-900">Player Availability</h3>
            <p className="text-gray-600">
              Players can easily mark their availability for games. Admins get real-time visibility for squad planning.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-white py-12 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">
            Get Started in Minutes
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Request Your Club Creation</h3>
              <p className="text-gray-600 mb-4">
                Sign up and request your club to be created. Our team will set up your club profile and get you started.
              </p>
              <button
                onClick={() => navigate('/register-club')}
                className="text-green-600 font-bold hover:underline"
              >
                Register Now →
              </button>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Invite Your Team</h3>
              <p className="text-gray-600">
                Share your club code with players. They can join instantly and start participating.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Start Managing</h3>
              <p className="text-gray-600">
                Create teams, schedule games, track expenses, and manage everything from one dashboard.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Transform Your Sports Club?
          </h2>
          <p className="text-xl mb-8 text-green-50">
            Sign up and start using ClubHudl to streamline your club operations.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => navigate('/login')}
              className="bg-white text-green-600 px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl transition-all"
            >
              Login to Your Club
            </button>
            <button
              onClick={() => navigate('/register-club')}
              className="bg-green-700 text-white border border-green-500 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-green-800 transition-all"
            >
              Register a New Club
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Trophy className="w-6 h-6 text-green-500" />
            <span className="text-xl font-bold text-white">
              <span className="text-green-500">Club</span><span className="text-blue-500">Hudl</span>
            </span>
          </div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">Your Club Operations Made Easy</p>
          <p>&copy; 2025 ClubHudl. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;